/**
 * Bottom "soft hints" panel. Multiple hints can show simultaneously.
 *
 * Mirrors `Ralph/ConnectionDiagnostics/PRD.md` → Slice 3 → "Bottom — soft hints".
 *
 * The dialog never claims "you are a contained user" — it shows the user the
 * facts and lets them or their DBA conclude.
 */

import type { DiagnosticsReport } from '../../../types/connectionDiagnostics';
import { lookupConnectionDiagnosticsError } from '../connectionDiagnosticsErrorMap';

type SoftHintsProps = {
  report: DiagnosticsReport;
  timedOut?: boolean;
  driverCode?: string;
};

type Hint = { key: string; text: string };

function collectHints(props: SoftHintsProps): Hint[] {
  const { report, timedOut, driverCode } = props;
  const hints: Hint[] = [];

  const reportTimedOut = timedOut || report.databases.listingTimedOut === true;
  if (reportTimedOut) {
    hints.push({
      key: 'timeout',
      text:
        'Listing query timeout. Try again — Hyperscale cold-cache or elastic-pool throttle can briefly delay the listing query.',
    });
  }

  // Direct signal: contained-user shape on Azure SQL — strongest hint we have
  // for the account where listing fails but per-DB queries work.
  if (report.capabilities?.hasMasterAccess === false) {
    hints.push({
      key: 'contained-user',
      text:
        'Account has no access to master DB. Some Azure SQL setups scope a principal to one user database, so listing all databases is blocked even though that user database opens fine.',
    });
  }

  const isSysadmin = report.capabilities?.isSysadmin;
  const canViewAnyDb = report.capabilities?.canViewAnyDatabase;
  const lowVisibility =
    isSysadmin === false &&
    canViewAnyDb === false &&
    (report.databases.rawCount === 0 ||
      (report.databases.listingError?.sqlErrorCode !== undefined &&
        [18456, 40531, 916].includes(report.databases.listingError.sqlErrorCode)));
  if (lowVisibility) {
    hints.push({
      key: 'permission',
      text:
        'Permission-restricted account. Share these facts with your DBA, or use Open database by name… for databases you already know.',
    });
  }

  // Engine edition signals that surface known visibility quirks.
  if (report.serverEdition?.engineEdition === 'azure_sql_managed_instance') {
    hints.push({
      key: 'azure-mi',
      text:
        'Azure SQL Managed Instance has different metadata-visibility rules than Azure SQL Database. Listing failures here are often resolved by an explicit GRANT VIEW ANY DATABASE on the server-level login.',
    });
  }

  const listingErr = report.databases.listingError;
  if (listingErr?.sqlErrorCode !== undefined) {
    const mapped = lookupConnectionDiagnosticsError({
      sqlErrorCode: listingErr.sqlErrorCode,
      code: driverCode,
      fallbackMessage: listingErr.message,
    });
    if (mapped.category !== 'other') {
      hints.push({ key: `mapped-${listingErr.sqlErrorCode}`, text: mapped.description });
    }
  }

  return hints;
}

export function DiagnosticsSoftHints(props: SoftHintsProps) {
  const hints = collectHints(props);
  if (hints.length === 0) return null;
  return (
    <ul data-testid="diag-soft-hints" className="space-y-1.5 text-sm text-muted-foreground">
      {hints.map((h) => (
        <li key={h.key} className="leading-snug">
          {h.text}
        </li>
      ))}
    </ul>
  );
}
